/**
 * `useWorkflows` — fetch the loaded WorkflowSpec registry once per session.
 *
 * The registry is process-wide on the server (loaded at plugin enable) and
 * does not change at runtime, so we cache aggressively: `staleTime` is set
 * to `Infinity` so React Query treats the response as immutable for the
 * session. Callers receive the raw `WorkflowSummary[]` plus a precomputed
 * id→displayName resolver for convenience.
 */
import { useQuery } from '@tanstack/react-query';

import { listAiWorkflows, type WorkflowSummary } from '../api/workflows';
import { aiQueryKeys } from './query-keys';

export interface UseWorkflowsResult {
  workflows: WorkflowSummary[];
  /** Resolve a workflow id to its display name. Returns undefined if not loaded yet or not found. */
  resolveDisplayName: (workflowId: string | undefined) => string | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const useWorkflows = (): UseWorkflowsResult => {
  const query = useQuery<WorkflowSummary[]>({
    queryKey: aiQueryKeys.workflows(),
    queryFn: listAiWorkflows,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const workflows = query.data ?? [];
  const byId = new Map(workflows.map((w) => [w.id, w]));

  return {
    workflows,
    resolveDisplayName: (workflowId) => {
      if (!workflowId) return undefined;
      return byId.get(workflowId)?.displayName;
    },
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
